import {
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  Badge,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { djs } from '@duke-hq/djs';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { ProviderUserRoleColorSchemes } from '@duke-hq/shared-constants/providers';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { MoreHorizontal, QrCode } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ReferralActivationInfo } from '../../../components/ReferralActivationInfo';
import { getStatesOptions } from '../../../students-page/constants';

type PaginatedReferralsResponse = RouterOutputs['courseReferral']['getAll'];
type PendingReferralsTableProps = {
  paginatedReferrals: PaginatedReferralsResponse;
};

function formatTime(date: Date) {
  const now = djs();
  const inputDate = djs(date);

  if (inputDate.isSame(now, 'day')) {
    return inputDate.format('hh:mm a');
  } else if (inputDate.isSame(now.subtract(1, 'day'), 'day')) {
    return `Yesterday, ${inputDate.format('hh:mm a')}`;
  } else if (inputDate.year() === now.year()) {
    return inputDate.format('D MMM, hh:mm a');
  } else {
    return inputDate.format('D MMM YYYY, hh:mm a');
  }
}

export function PendingReferralsTable({ paginatedReferrals }: PendingReferralsTableProps) {
  const trpcUtils = trpcTenant.useUtils();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const referralId = searchParams.get('referralId');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activationInfo, setActivationInfo] = useState<
    RouterOutputs['courseReferral']['getActivationInfo'] | null
  >(null);
  const [isLoadingActivationInfo, setIsLoadingActivationInfo] = useState(false);
  const [isDeleteConfirmationAlertOpen, setIsDeleteConfirmationAlertOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] =
    useState<PaginatedReferralsResponse['items'][number]>();
  const deleteReferral = trpcTenant.courseReferral.deleteReferral.useMutation();

  const fetchActivationInfo = useCallback(
    async (referralId: string) => {
      setIsLoadingActivationInfo(true);
      setActivationInfo(null);
      try {
        const fetchedActivationInfo = await trpcUtils.courseReferral.getActivationInfo.fetch({
          referral_id: referralId,
        });
        setActivationInfo(fetchedActivationInfo);
      } catch (error) {
        toast({
          title: t`Failed to fetch activation details`,
          description: `${error instanceof Error ? error.message : t`An unexpected error occurred`}`,
          status: 'error',
          duration: 7000,
        });
      } finally {
        setIsLoadingActivationInfo(false);
      }
    },
    [trpcUtils.courseReferral.getActivationInfo, toast],
  );

  const handleActivationDetailsClick = useCallback(
    (referralId: string) => {
      setSearchParams((prev) => {
        prev.set('referralId', referralId);
        return prev;
      });
      onOpen();
      void fetchActivationInfo(referralId);
    },
    [setSearchParams, onOpen, fetchActivationInfo],
  );

  useEffect(() => {
    if (referralId) {
      const referral = paginatedReferrals.items.find((r) => r.id === referralId);
      if (referral && !isOpen) {
        handleActivationDetailsClick(referral.id);
      }
    }
  }, [referralId, paginatedReferrals, handleActivationDetailsClick, onOpen, isOpen]);

  const handleCloseModal = useCallback(() => {
    onClose();
    setSearchParams((prev) => {
      prev.delete('referralId');
      return prev;
    });
  }, [onClose, setSearchParams]);

  const stateOptions = getStatesOptions();

  const referralRows = useMemo(
    () =>
      paginatedReferrals.items.map((referral) => {
        return (
          <Tr key={referral.id} _hover={{ bg: 'bg.subtle' }}>
            <Td maxW={'240px'} px={4}>
              <Text
                fontWeight="medium"
                fontSize="sm"
                color="fg.default"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {referral.course?.title || 'N/A'}
              </Text>
              {referral.course?.identifier ? (
                <Text fontSize="sm" color="fg.muted">
                  {referral.course.identifier}
                </Text>
              ) : null}
            </Td>
            <Td px={4}>
              <Text fontSize="sm" color="fg.muted">
                {referral.external_id || 'N/A'}
              </Text>
            </Td>
            <Td px={4}>
              <Text fontSize="sm" color="fg.emphasis">
                {formatTime(referral.created_at)}
              </Text>
            </Td>
            <Td px={4}>
              {referral.consultant ? (
                <Stack>
                  <Text fontSize="sm" color="fg.default">
                    {referral.consultant.first_name} {referral.consultant.last_name}
                  </Text>
                  <Badge
                    borderRadius="full"
                    colorScheme={ProviderUserRoleColorSchemes[referral.consultant.role] ?? 'gray'}
                    variant="muted"
                    textTransform="none"
                    width="min-content"
                  >
                    {referral.consultant.role.replace(/_/g, ' ')}
                  </Badge>
                </Stack>
              ) : (
                'N/A'
              )}
            </Td>

            <Td px={4}>
              <Text fontSize="sm" color="fg.default" fontWeight="medium">
                {referral.siteLocation?.name ?? 'N/A'}
              </Text>
              {referral.siteLocation?.state ? (
                <Text>
                  {stateOptions.find((state) => state.value === referral.siteLocation?.state)
                    ?.label ?? referral.siteLocation.state}
                </Text>
              ) : null}
            </Td>
            <Td maxW={'120px'} px={4}>
              <Text fontSize="sm" color="fg.default" whiteSpace="wrap">
                {referral.enrolment_contract_type}
              </Text>
            </Td>
            <Td px={4}>
              <HStack>
                <Button
                  size="sm"
                  bg="bg.muted"
                  color="fg.emphasis"
                  onClick={() => handleActivationDetailsClick(referral.id)}
                  leftIcon={<QrCode size={16} />}
                >
                  <Trans>Info</Trans>
                </Button>
                <Menu>
                  <MenuButton
                    aria-label={t`More options for course referral`}
                    as={IconButton}
                    color="fg.emphasis"
                    icon={<MoreHorizontal size="20px" />}
                    variant="unstyled"
                    onClick={() => setSelectedReferral(referral)}
                  />
                  <MenuList>
                    <MenuItem onClick={() => setIsDeleteConfirmationAlertOpen(true)}>
                      <Trans>Delete referral</Trans>
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </Td>
          </Tr>
        );
      }),
    [paginatedReferrals.items, stateOptions, handleActivationDetailsClick],
  );

  const handleDelete = useCallback(async () => {
    try {
      if (!selectedReferral) {
        return;
      }
      await deleteReferral.mutateAsync({ referral_id: selectedReferral.id });
      await trpcUtils.courseReferral.getAll.invalidate();
      await trpcUtils.courseReferral.countByStatus.invalidate();
      setIsDeleteConfirmationAlertOpen(false);
    } catch (error) {
      toast({
        title: t`Failed to delete referral`,
        description: `${error instanceof Error ? error.message : t`An unexpected error occurred`}`,
        status: 'error',
        duration: 7000,
      });
    }
  }, [
    deleteReferral,
    selectedReferral,
    toast,
    trpcUtils.courseReferral.countByStatus,
    trpcUtils.courseReferral.getAll,
  ]);

  const deleteDialogContent = useMemo(() => {
    if (!selectedReferral) {
      return (
        <Text fontSize="sm">
          <Trans>We couldn't find the referral you're trying to delete. Please try again.</Trans>
        </Text>
      );
    }
    return (
      <VStack spacing="4" justify="start" align="start">
        <Text>
          <Trans>You are about to delete this referral. Are you sure you want to continue?</Trans>
        </Text>
        <VStack
          spacing="1"
          justify="start"
          align="start"
          bg="bg.subtle"
          borderRadius="8px"
          w="full"
          p="8px"
        >
          <Text fontWeight="semibold" fontSize="sm" color="fg.default">
            {selectedReferral.course?.title || 'N/A'}
          </Text>
          {selectedReferral.course?.identifier ? (
            <Text fontSize="sm" color="fg.emphasis">
              {selectedReferral.course.identifier}
            </Text>
          ) : null}
          <Text fontSize="xs" color="fg.muted">
            {selectedReferral.external_id || 'N/A'}
          </Text>
        </VStack>
      </VStack>
    );
  }, [selectedReferral]);

  return (
    <>
      <Table>
        <Thead>
          <Tr>
            <Th px={4}>
              <Trans>Course</Trans>
            </Th>
            <Th w="140px" px={4}>
              <Trans>Purchase order /UI</Trans>
            </Th>
            <Th px={4}>
              <Trans>Created</Trans>
            </Th>
            <Th px={4}>
              <Trans>Consultant</Trans>
            </Th>
            <Th px={4}>
              <Trans>Site</Trans>
            </Th>
            <Th px={4}>
              <Trans>Contract</Trans>
            </Th>
            <Th px={4}></Th>
          </Tr>
        </Thead>
        <Tbody>{referralRows}</Tbody>
      </Table>

      <Modal
        onClose={handleCloseModal}
        isOpen={isOpen}
        isCentered
        closeOnOverlayClick={false}
        size="3xl"
      >
        <ModalOverlay />
        <ModalContent h="auto" bg="bg.muted">
          <ModalHeader></ModalHeader>
          <ModalCloseButton size="xs" />
          <ModalBody h="full" p="0px" m="0px">
            {isLoadingActivationInfo ? (
              <Flex w="full" h="300px" justify="center" align="center">
                <Spinner size="xl" />
              </Flex>
            ) : activationInfo ? (
              <ReferralActivationInfo activationInfo={activationInfo} />
            ) : (
              <Flex w="full" h="300px" px="24px" py="8px" justify="center" align="center">
                <Text fontSize="sm">
                  <Trans>
                    We couldn't find the referral you're trying to access. Please try again.
                  </Trans>
                </Text>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <ConfirmationDialog
        isOpen={isDeleteConfirmationAlertOpen}
        onClose={() => setIsDeleteConfirmationAlertOpen(false)}
        title={<Trans>Delete Course Referral</Trans>}
        body={deleteDialogContent}
        onConfirm={handleDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
        isLoading={deleteReferral.isPending}
      />
    </>
  );
}
